import { useState, useCallback, useRef } from "react";

const useChatState = () => {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const messageIdCounter = useRef(0);

  const generateMessageId = useCallback(() => {
    return `msg_${Date.now()}_${++messageIdCounter.current}`;
  }, []);

  const addMessage = useCallback(
    (message) => {
      const newMessage = {
        id: message.id || generateMessageId(),
        content: message.content || "",
        role: message.role || "user",
        timestamp: message.timestamp || new Date().toISOString(),
        status: message.status || "delivered",
        attachments: message.attachments || [],
      };

      setMessages((prev) => [...prev, newMessage]);
      return newMessage.id;
    },
    [generateMessageId]
  );

  const updateMessage = useCallback((messageId, updates) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === messageId ? { ...msg, ...updates } : msg))
    );
  }, []);

  const removeMessage = useCallback((messageId) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const getLastMessage = useCallback(() => {
    return messages[messages.length - 1] || null;
  }, [messages]);

  const getMessagesByRole = useCallback(
    (role) => {
      return messages.filter((msg) => msg.role === role);
    },
    [messages]
  );

  const getMessageCount = useCallback(() => {
    return messages.length;
  }, [messages]);

  const hasUnreadMessages = useCallback(
    (lastReadTimestamp) => {
      if (!lastReadTimestamp) return messages.length > 0;

      return messages.some(
        (msg) => new Date(msg.timestamp) > new Date(lastReadTimestamp)
      );
    },
    [messages]
  );

  return {
    messages,
    isTyping,
    setIsTyping,
    addMessage,
    updateMessage,
    removeMessage,
    clearMessages,
    getLastMessage,
    getMessagesByRole,
    getMessageCount,
    hasUnreadMessages,
  };
};

export { useChatState };
